import React, { useState } from 'react';
import { Bug, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BugReportButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('mandala-token');
      const res = await fetch(`${API}/bug-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          title,
          description,
          page_url: window.location.href,
          browser_info: navigator.userAgent
        })
      });

      if (!res.ok) throw new Error('Failed to submit report');
      
      toast.success('Bug report submitted! Thank you for your feedback.');
      setIsOpen(false);
      setTitle('');
      setDescription('');
    } catch (err) {
      toast.error('Failed to submit bug report');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button - positioned to avoid Emergent badge and navigation buttons on mobile */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-14 md:right-6 z-[9999] p-3 md:p-4 bg-orange-600 hover:bg-orange-700 text-white rounded-full shadow-lg transition-all hover:scale-110"
        title="Report a Bug"
        data-testid="bug-report-btn"
      >
        <Bug size={20} className="md:w-6 md:h-6" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug className="text-orange-500" size={20} />
                <h3 className="text-lg font-semibold text-white">Report a Bug</h3>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief description of the issue..."
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="bug-title-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe what happened, steps to reproduce, and what you expected..."
                  className="w-full h-32 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="bug-description-input"
                />
              </div>

              <p className="text-xs text-gray-500">
                Current page and browser info will be included automatically.
              </p>

              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setIsOpen(false)}
                  className="border-gray-600"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={sending}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="submit-bug-btn"
                >
                  <Send size={16} className="mr-2" />
                  {sending ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;
